"use client";

import Script from "next/script";
import { useEffect, useRef, useCallback, useSyncExternalStore } from "react";
import { useAuthSync } from "@/hooks/use-auth-sync";

declare global {
  interface Window {
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (id: string, data: Record<string, unknown>) => void;
      reset: () => void;
      toggle: (state?: "open" | "close") => void;
    };
  }
}

const CURRENT_USER_QUERY = `{
  currentUser {
    _Id
    id
    slug
    fullname
    email
    phoneNumber
    gender
    avatar { contentUrl }
    city
    bio
    settings { language }
    circleMemberships {
      edges {
        node {
          circle {
            name
            networks {
              edges {
                node {
                  subscription { plan { slug } }
                }
              }
            }
          }
        }
      }
    }
  }
}`;

/**
 * Paid plan slugs, mirroring `subscription_plan.slug` on the API (Réseau upward; Communauté is
 * the free tier). Duplicated from front/src/domain/subscription/plans.ts — the two apps share no
 * code — so keep them in step.
 */
const PAID_PLAN_SLUGS = ["network", "signature"];

interface CircleNode {
  name?: string;
  networks?: {
    edges?: Array<{
      node?: {
        subscription?: { plan?: { slug?: string } };
      };
    }>;
  };
}

interface CurrentUser {
  _Id: string;
  id: string;
  slug: string;
  fullname: string;
  email?: string;
  phoneNumber?: { internationalNumber?: string } | null;
  gender?: string;
  avatar?: { contentUrl: string } | null;
  city?: string;
  bio?: string;
  settings?: { language?: string } | null;
  circleMemberships?: {
    edges?: Array<{ node?: { circle?: CircleNode } }>;
  };
}

export function ChatwootWidget() {
  const baseUrl = process.env.NEXT_PUBLIC_CHATWOOT_BASE_URL;
  const websiteToken = process.env.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN;
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  const appUrl = process.env.NEXT_PUBLIC_APP_URL || "https://communo.app";

  const { user, accessToken, isAuthenticated } = useAuthSync();
  const identifiedRef = useRef(false);
  const prevAuthRef = useRef(false);

  // Ready as soon as window.$chatwoot exists — covers both the cached-script
  // case (already set before mount) and the chatwoot:ready event.
  const chatwootReady = useSyncExternalStore(
    (onChange) => {
      window.addEventListener("chatwoot:ready", onChange);
      return () => window.removeEventListener("chatwoot:ready", onChange);
    },
    () => Boolean(window.$chatwoot),
    () => false,
  );

  const identifyUser = useCallback(async () => {
    if (!apiUrl || !accessToken || !user) return;

    try {
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ query: CURRENT_USER_QUERY }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const { data } = await res.json();
    
      const cu: CurrentUser | undefined = data?.currentUser;
      if (!cu) throw new Error("no currentUser in response");

      const circles = (cu.circleMemberships?.edges ?? []).map(
        (e) => e.node?.circle
      );
      // Keyed on the plan slug, not on its name: names are translated, so lowercasing them only
      // ever matched the French labels. Slugs mirror `subscription_plan.slug` on the API.
      const proCommunities = circles.filter((c) =>
        PAID_PLAN_SLUGS.includes(
          c?.networks?.edges?.[0]?.node?.subscription?.plan?.slug ?? ""
        )
      );

      const userData = {
        email: cu.email,
        name: cu.fullname,
        phone_number: cu.phoneNumber?.internationalNumber,
        avatar_url: cu.avatar?.contentUrl,
        city: cu.city,
        description: cu.bio ?? "",
        company_name: proCommunities.map((c) => c?.name).filter(Boolean).join(", "),
        custom_attributes: {
          gender: cu.gender,
          language: cu.settings?.language,
          communities: circles.map((c) => c?.name).filter(Boolean).join(", "),
          profile: `${appUrl}/u/${cu.slug}`,
        },
      };

      window.$chatwoot?.setUser(cu._Id, userData);
    } catch {
      window.$chatwoot?.setUser(user.id, {
        name: user.fullname,
        avatar_url: user.avatar?.contentUrl,
      });
    }
  }, [apiUrl, accessToken, user, appUrl]);

  useEffect(() => {
    if (!chatwootReady) return;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    if (isAuthenticated && !identifiedRef.current) {
      identifiedRef.current = true;
      identifyUser();
      // Retry once after 1.5s: on first load the Chatwoot WS connection may not
      // be established yet when the initial setUser call arrives.
      retryTimer = setTimeout(() => identifyUser(), 1500);
    }

    if (!isAuthenticated && prevAuthRef.current) {
      identifiedRef.current = false;
      window.$chatwoot?.reset();
    }

    prevAuthRef.current = isAuthenticated;

    return () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [chatwootReady, isAuthenticated, identifyUser]);

  if (!baseUrl || !websiteToken) return null;

  return (
    <Script
      src={`${baseUrl}/packs/js/sdk.js`}
      strategy="afterInteractive"
      onLoad={() => {
        window.chatwootSDK?.run({ websiteToken: websiteToken!, baseUrl: baseUrl! });
      }}
    />
  );
}
